import { z } from 'zod';

// ===================================
// HELPERS PARA RUT CHILENO
// ===================================

/**
 * Normaliza un RUT chileno removiendo puntos y guiones
 * Ejemplo: "12.345.678-9" → "123456789"
 */
export const normalizarRut = (rut: string): string => {
  return rut.replace(/\./g, '').replace(/-/g, '').trim().toUpperCase();
};

/**
 * Formatea un RUT chileno con puntos y guión
 * Ejemplo: "123456789" → "12.345.678-9"
 */
export const formatearRut = (rut: string): string => {
  const rutNormalizado = normalizarRut(rut);
  if (rutNormalizado.length < 2) return rutNormalizado;

  const dv = rutNormalizado.slice(-1);
  const numero = rutNormalizado.slice(0, -1);

  // Formatear con puntos (de derecha a izquierda)
  const numeroFormateado = numero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${numeroFormateado}-${dv}`;
};

/**
 * Calcula el dígito verificador de un RUT chileno
 */
const calcularDV = (rutSinDV: string): string => {
  let suma = 0;
  let multiplo = 2;

  for (let i = rutSinDV.length - 1; i >= 0; i--) {
    suma += parseInt(rutSinDV[i]) * multiplo;
    multiplo = multiplo === 7 ? 2 : multiplo + 1;
  }

  const resto = suma % 11;
  const dv = 11 - resto;

  if (dv === 11) return '0';
  if (dv === 10) return 'K';
  return dv.toString();
};

/**
 * Valida si un RUT chileno es válido (con dígito verificador correcto)
 */
export const validarRut = (rut: string): boolean => {
  if (!rut || rut.trim() === '') return false;

  const rutNormalizado = normalizarRut(rut);

  // Debe tener al menos 2 caracteres (número + DV)
  if (rutNormalizado.length < 2) return false;

  // Separar número y DV
  const dv = rutNormalizado.slice(-1);
  const numero = rutNormalizado.slice(0, -1);

  // El número debe ser numérico
  if (!/^\d+$/.test(numero)) return false;

  // Calcular DV esperado y comparar
  const dvCalculado = calcularDV(numero);
  return dv === dvCalculado;
};

// ===================================
// SCHEMAS ZOD
// ===================================

/**
 * Schema para validar RUT chileno con dígito verificador
 */
const rutSchema = z.string()
  .min(1, 'El RUT es requerido')
  .max(20, 'El RUT es demasiado largo')
  .refine(validarRut, {
    message: 'RUT inválido. Debe ser un RUT chileno válido con dígito verificador correcto (ej: 12.345.678-9)',
  });

/**
 * Schema para detalle de factura
 */
export const detalleFacturaSchema = z.object({
  ingrediente_id: z.number().int().positive().nullable(),
  nombre_ingrediente_temporal: z.string()
    .min(1, 'El nombre del ingrediente es requerido')
    .max(255, 'El nombre es demasiado largo'),
  marca: z.string().max(100).optional().nullable(),
  categoria_nombre: z.string().optional(),
  categoria_id: z.number().int().positive().nullable().optional(),
  cantidad: z.number()
    .positive('La cantidad debe ser mayor a 0')
    .min(0.01, 'La cantidad mínima es 0.01'),
  unidad_medida: z.string()
    .min(1, 'La unidad de medida es requerida')
    .max(50, 'La unidad de medida es demasiado larga'),
  precio_unitario: z.number()
    .nonnegative('El precio no puede ser negativo')
    .min(0, 'El precio mínimo es 0'),
  subtotal: z.number()
    .nonnegative('El subtotal no puede ser negativo'),
  // observaciones puede ser null al venir de MySQL en modo edición
  observaciones: z.string().nullish().transform((v) => v ?? undefined),
  // Campos de pack/caja
  // es_pack puede llegar como 0/1 de MySQL — se convierte a boolean
  es_pack: z
    .union([z.boolean(), z.number()])
    .transform((v) => Boolean(v))
    .optional()
    .default(false),
  cantidad_packs: z.number()
    .positive('La cantidad de packs debe ser mayor a 0')
    .optional()
    .nullable(),
  unidades_por_pack: z.number()
    .positive('Las unidades por pack deben ser mayores a 0')
    .optional()
    .nullable(),
  precio_pack: z.number()
    .nonnegative('El precio del pack no puede ser negativo')
    .optional()
    .nullable(),
  formato_compra: z.string()
    .max(100, 'El formato de compra es demasiado largo')
    .optional()
    .nullable(),
  // Campos de jerarquía de 3 niveles (NUEVO)
  jerarquia_niveles: z.number().int().min(0).max(3).optional().nullable(),
  nivel1_cantidad: z.number().positive().optional().nullable(),
  nivel1_nombre: z.string().max(50).optional().nullable(),
  nivel1_unidades: z.number().positive().optional().nullable(),
  nivel2_nombre: z.string().max(50).optional().nullable(),
  nivel2_cantidad_base: z.number().positive().optional().nullable(),
  precio_nivel1: z.number().nonnegative().optional().nullable(),
  precio_nivel2: z.number().nonnegative().optional().nullable(),
  precio_base: z.number().nonnegative().optional().nullable(),
  descripcion_jerarquia: z.string().max(255).optional().nullable(),
}).refine(
  (data) => {
    const nivel = data.jerarquia_niveles ?? 0;

    // Validar jerarquía nivel 3
    if (nivel === 3) {
      if (!data.nivel1_cantidad || !data.nivel1_nombre || !data.nivel1_unidades ||
          !data.nivel2_cantidad_base || !data.precio_nivel1) {
        return false;
      }
      const cantidadCalculada = data.nivel1_cantidad * data.nivel1_unidades * data.nivel2_cantidad_base;
      return Math.abs(data.cantidad - cantidadCalculada) < 0.01;
    }

    // Validar jerarquía nivel 2
    if (nivel === 2) {
      if (!data.nivel1_cantidad || !data.nivel1_nombre || !data.nivel1_unidades || !data.precio_nivel1) {
        return false;
      }
      const cantidadCalculada = data.nivel1_cantidad * data.nivel1_unidades;
      return Math.abs(data.cantidad - cantidadCalculada) < 0.01;
    }

    // Validación LEGACY para packs (cuando no hay jerarquía nueva)
    if (data.es_pack && nivel === 0) {
      if (data.cantidad_packs == null || data.unidades_por_pack == null) {
        return false;
      }
      const cantidadCalculada = data.cantidad_packs * data.unidades_por_pack;
      return Math.abs(data.cantidad - cantidadCalculada) < 0.01;
    }

    return true;
  },
  {
    message: 'Los datos de agrupamiento no son coherentes. Verifique las cantidades calculadas.',
    path: ['cantidad'],
  }
);

/**
 * Schema principal para el formulario del wizard de factura
 */
export const wizardFacturaSchema = z.object({
  // Paso 1: Datos de la factura
  numero_factura: z.string()
    .min(1, 'El número de factura es requerido')
    .max(100, 'El número de factura es demasiado largo'),

  proveedor_id: z.number().int().positive().nullable(),

  rut_proveedor: rutSchema,

  nombre_proveedor: z.string()
    .min(1, 'El nombre del proveedor es requerido')
    .max(255, 'El nombre del proveedor es demasiado largo'),

  fecha_emision: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido (use YYYY-MM-DD)'),

  metodo_pago: z.enum(['efectivo', 'transferencia', 'cheque', 'credito', 'debito'] as const, {
    error: 'Método de pago inválido',
  }),

  orden_compra_id: z.number().int().positive().nullable(),

  observaciones: z.string().max(500, 'Las observaciones son demasiado largas').optional(),

  // Indicador de si los valores incluyen IVA
  valores_incluyen_iva: z.boolean().default(false),

  // Paso 2: Detalles
  detalles: z.array(detalleFacturaSchema)
    .min(1, 'Debe agregar al menos un ingrediente'),

  // Metadatos OCR
  es_ocr: z.boolean().optional(),
  imagen_url: z.string().optional(),
});

/**
 * Tipo TypeScript inferido del schema
 */
export type WizardFacturaFormData = z.infer<typeof wizardFacturaSchema>;

/**
 * Schema parcial para validación por pasos
 */
export const wizardFacturaPaso1Schema = wizardFacturaSchema.pick({
  numero_factura: true,
  rut_proveedor: true,
  nombre_proveedor: true,
  fecha_emision: true,
  metodo_pago: true,
  observaciones: true,
  valores_incluyen_iva: true,
});

export const wizardFacturaPaso2Schema = wizardFacturaSchema.pick({
  detalles: true,
});

/**
 * Tipo para los datos de paso 1
 */
export type WizardFacturaPaso1Data = z.infer<typeof wizardFacturaPaso1Schema>;

/**
 * Tipo para los datos de paso 2
 */
export type WizardFacturaPaso2Data = z.infer<typeof wizardFacturaPaso2Schema>;
